import React from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import gql from 'graphql-tag';

import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { RCStarRating } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { SHOP_PATH } from '@reverbdotcom/commons/src/url_helpers';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';

import URLHelpers, { adminEditUserPath } from './shared/url_helpers';
import { BumpKey, FeaturedListingShopInfoData } from '../schemas/rql';
import { getYear } from '../lib/date_utils';
import { formatOrdersCount } from './item2_shop_stats';
import ShopPoliciesModal from './item2_shop_policies_modal';

interface IShopStatsProps {
  orderCount: number;
  createdAt: string;
}

interface IProps {
  listing: FeaturedListingShopInfoData.Fragment & BumpKey.Fragment;
}

function ShopStats(props: IShopStatsProps) {
  const { createdAt, orderCount } = props;

  const foundingYear = getYear(createdAt);
  const count = formatOrdersCount(orderCount);

  return (
    <div className="mt-half" data-shop-stats>
      <div className="size-80 color-secondary lh-130 d-flex">
        {count >= 10 && I18n.t('discovery.item2Shop.salesSince', { count: count.toLocaleString(), foundingYear })}
      </div>
    </div>
  );
}

export const FeaturedListingShopInfoFragment = gql`
  fragment FeaturedListingShopInfoData on Listing {
    _id
    id
    state
    seller {
      _id
      id
      uuid
      feedbackSummary {
        rollingRatingPercentage
        receivedCount
      }
    }
    shop {
      _id
      name
      slug
      preferredSeller
      quickShipper
      quickResponder
      address {
        displayLocation
        countryCode
      }
      paymentPolicy
      createdAt
      orders {
        total
      }
    }
    shipping {
      localPickupOnly
    }
  }
`;

export function FeaturedListingShopInfo(props: IProps) {
  const { listing } = props;
  const { shop, seller } = listing;

  const currentUser = useUser();
  if (!shop) return null;

  const { preferredSeller, quickResponder, quickShipper } = shop;
  const messageURL = URLHelpers.newDashboardMessagesPath(
    props.listing.id,
    seller.uuid,
    props.listing.bumpKey?.key,
  );

  const secondaryClasses = classNames(
    'item2-shop__secondary',
    { 'item2-shop__secondary--wide':
      (preferredSeller || quickResponder || quickShipper),
    },
  );

  return (
    <div className="item2-shop" id={props.listing.id}>
      <div className="item2-shop__primary">
        <CoreLink
          to={SHOP_PATH.expand({ slug: shop.slug })}
          className="weight-bold lh-130 hyphenate color-primary"
        >
          {shop.name}
        </CoreLink>
        <div data-location className="size-80 color-secondary lh-130 mt-half">
          {shop.address.displayLocation}
          {currentUser.isAdmin &&
            <CoreLink
              to={adminEditUserPath.expand({ id: seller.id })}
              className="ml-half"
              data-user-edit
            >
              {I18n.t('discovery.item2Shop.viewUser')}
            </CoreLink>
          }
        </div>
        <div className="mtb-half">
          <RCStarRating
            rating={seller.feedbackSummary.rollingRatingPercentage / 100}
            count={seller.feedbackSummary.receivedCount}
          />
        </div>
        <div className="mt-1">
          <ShopStats
            orderCount={shop.orders.total}
            createdAt={shop.createdAt}
          />
        </div>
      </div>
      <div className={secondaryClasses}>
        <div className="item2-shop__button-container">
          <a
            href={messageURL}
            className="item2-shop__button--featured-listing"
            data-registration="required"
            data-send-message
          >
            {I18n.t('discovery.item2Shop.messageSeller')}
          </a>
        </div>
        <div className="item2-shop__button-container">
          <ShopPoliciesModal
            listingId={props.listing.id}
            buttonClassOverride="item2-shop__button--featured-listing"
            clickEvent={{
              eventName: MParticleEventName.ClickedI2ShopPoliciesModal,
              componentName: 'Item2Shop',
              listingId: props.listing.id,
            }}
          />
        </div>
      </div>
    </div>
  );
}
